import { reactive, ref, computed } from "vue";
import axios from "axios";
import { useStore } from "vuex";
import { useToast } from "primevue/usetoast";
import { API_BASE_URL } from "@/consts";

// Not yet applied: the lambda picks both up, 'retry' gets one more try
export const OPEN_RATE_CHANGE_STATUSES = ["pending", "retry"];

// Scheduled CPM changes of one seller, shared by the contract form and the history tab
export function useSellerRateChanges(sellerId, partnerId) {
  const store = useStore();
  const toast = useToast();

  const tokenKey = store.getters.isAdminLoggedIn ? "adminToken" : "token";
  const config = {
    headers: {
      Authorization: `Bearer ${localStorage.getItem(tokenKey)}`,
      partnerid: partnerId,
    },
  };
  const url = `${API_BASE_URL}/sellers/${sellerId}/rate-changes`;

  const changes = ref([]);
  const loading = ref(false);
  const saving = ref(false);

  const openChanges = computed(() =>
    changes.value
      .filter((change) => OPEN_RATE_CHANGE_STATUSES.includes(change.status))
      .sort((a, b) => a.effective_at.localeCompare(b.effective_at))
  );

  const showError = (error) =>
    toast.add({
      severity: "error",
      summary: "Error",
      detail: error.response?.data?.message || "Something went wrong",
      life: 3000,
    });

  const load = async () => {
    loading.value = true;
    try {
      const { data } = await axios.get(url, config);
      changes.value = data.data;
    } catch (error) {
      showError(error);
    } finally {
      loading.value = false;
    }
  };

  // Both endpoints answer with the full refreshed list
  const save = async (request) => {
    saving.value = true;
    try {
      const { data } = await request();
      changes.value = data.data;
      return true;
    } catch (error) {
      showError(error);
      return false;
    } finally {
      saving.value = false;
    }
  };

  const schedule = (newValue, effectiveAt) =>
    save(() =>
      axios.post(
        url,
        { newValue, effectiveAt: effectiveAt.toISOString() },
        config
      )
    );

  // A failed cancel usually means the lambda got there first: show the real state
  const cancel = async (changeId) => {
    const cancelled = await save(() =>
      axios.post(`${url}/${changeId}/cancel`, {}, config)
    );
    if (!cancelled) await load();
    return cancelled;
  };

  return reactive({
    changes,
    openChanges,
    loading,
    saving,
    load,
    schedule,
    cancel,
  });
}
